import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsUp, Sparkles, Swords, Shield, Heart, Wind } from "lucide-react";

const STATS = [
  { Icon: Swords, label: "ATK" },
  { Icon: Shield, label: "DEF" },
  { Icon: Heart, label: "HP" },
  { Icon: Wind, label: "SPD" },
];

// Celebratory overlay that fires once when a card levels up after a battle.
// `levelUps` = [{ card_id, title, level, maxed }]
export function LevelUpCelebration({ levelUps }) {
  const [shown, setShown] = useState([]);
  const seenKey = useRef(null);

  useEffect(() => {
    if (!levelUps || levelUps.length === 0) return;
    const key = levelUps.map((l) => `${l.card_id}:${l.level}`).join("|");
    if (key === seenKey.current) return;
    seenKey.current = key;
    setShown(levelUps);
  }, [levelUps]);
  useEffect(() => {
    if (!shown.length) return undefined;
    const t = setTimeout(() => setShown([]), 4600);
    return () => clearTimeout(t);
  }, [shown]);

  return (
    <AnimatePresence>
      {shown.length > 0 && (
        <motion.div
          data-testid="levelup-celebration"
          className="fixed inset-0 z-[120] flex flex-col items-center justify-center gap-3 pointer-events-none px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShown([])}
        >
          <motion.div
            className="absolute inset-0 bg-emerald-400/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0] }}
            transition={{ duration: 0.6 }}
          />
          {shown.slice(0, 3).map((lu, i) => (
            <motion.div
              key={lu.card_id}
              data-testid={`levelup-card-${lu.card_id}`}
              initial={{ opacity: 0, y: 40, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: i * 0.18, type: "spring", stiffness: 240, damping: 18 }}
              className="relative rounded-2xl border border-emerald-300/50 bg-[#0b1411]/95 backdrop-blur-xl px-7 py-5 text-center shadow-[0_0_50px_rgba(52,211,153,0.4)]"
            >
              <motion.div
                className="flex items-center justify-center gap-2 text-emerald-300"
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 1.1 }}
              >
                <ChevronsUp className="h-5 w-5" />
                <span className="font-display tracking-[0.3em] text-sm">LEVEL UP</span>
                <ChevronsUp className="h-5 w-5" />
              </motion.div>
              <div className="font-display text-2xl text-white mt-1.5">{lu.title}</div>
              <div className="font-mono text-[13px] text-emerald-300 mt-0.5 flex items-center justify-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                {lu.maxed ? "MAX LEVEL — Lv 10" : `Now Level ${lu.level}`}
              </div>
              <div className="flex items-center justify-center gap-3 mt-3">
                {STATS.map(({ Icon, label }) => (
                  <motion.div
                    key={label}
                    className="flex flex-col items-center gap-0.5"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.18 + 0.3 }}
                  >
                    <Icon className="h-3.5 w-3.5 text-white/50" />
                    <span className="font-mono text-[11px] text-emerald-300 font-semibold">+5%</span>
                    <span className="font-mono text-[7px] uppercase tracking-wider text-white/35">{label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
          {shown.length > 3 && (
            <div className="font-mono text-[11px] text-white/50">+{shown.length - 3} more leveled up</div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
